import { Box, Popper, useTheme } from '@mui/material';
import { portalPaths } from '@provision/portal-routes';
import { ArrowUpRight, Info, Sparkles } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  cardSx,
  emptySourceSx,
  headerSx,
  headerValueSx,
  reasoningBodySx,
  reasoningLabelSx,
  referenceIdSx,
  referenceRowSx,
  referencesListSx,
  referenceSubjectSx,
  scrollBodySx,
  sourceHeadingSx,
  triggerButtonSx,
  triggerWrapSx,
} from './reasoning-popover.styles';

const HIDE_DELAY_MS = 180;
const EMPTY_CITATION_IDS: string[] = [];

export interface CitedConversation {
  friendlyId: string;
  subject: string;
}

export const ReasoningPopover = ({
  label,
  value,
  paragraphs,
  references,
  citationIds = EMPTY_CITATION_IDS,
  citationsLoading = false,
}: {
  label: 'Overall health' | 'Site sentiment';
  value: React.ReactNode;
  paragraphs: string[];
  references: CitedConversation[];
  citationIds?: string[];
  citationsLoading?: boolean;
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [pinned, setPinned] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const open = Boolean(anchor);
  const text = paragraphs.filter(Boolean);
  const paragraphKeys = new Map<string, number>();
  const paragraphKey = (paragraph: string) => {
    const n = paragraphKeys.get(paragraph) ?? 0;
    paragraphKeys.set(paragraph, n + 1);
    return n === 0 ? paragraph : `${paragraph}#${n}`;
  };
  const edgePadding = Number.parseInt(theme.spacing(1), 10);
  const cited = citationIds.filter(Boolean);
  const showReferences = references.length > 0;
  const showLoadingCitations = citationsLoading && cited.length > 0 && !showReferences;
  const showUnavailableCitations = !citationsLoading && cited.length > 0 && !showReferences;

  const panel = (anchor?.closest('[data-site-overview-panel]') as HTMLElement | null) ?? null;
  const maxHeight = useMemo(() => {
    if (!anchor) {
      return undefined;
    }
    const rect = anchor.getBoundingClientRect();
    const above = rect.top - edgePadding * 2;
    const below = window.innerHeight - rect.bottom - edgePadding * 2;
    return Math.max(0, Math.min(520, window.innerHeight * 0.7, Math.max(above, below)));
  }, [anchor, edgePadding]);

  const popperAnchor = useMemo(() => {
    if (!anchor) {
      return null;
    }
    if (!panel) {
      return anchor;
    }

    return {
      getBoundingClientRect: () => {
        const trigger = anchor.getBoundingClientRect();
        const box = panel.getBoundingClientRect();
        const x = box.left + box.width / 2;
        return {
          width: 0,
          height: trigger.height,
          top: trigger.top,
          bottom: trigger.bottom,
          left: x,
          right: x,
          x,
          y: trigger.top,
          toJSON: () => ({}),
        };
      },
      contextElement: panel,
    };
  }, [anchor, panel]);

  const cancelHide = useCallback(() => clearTimeout(hideTimer.current), []);
  const close = useCallback(() => {
    cancelHide();
    setPinned(false);
    setAnchor(null);
  }, [cancelHide]);
  const scheduleHide = useCallback(() => {
    if (pinned) {
      return;
    }
    cancelHide();
    hideTimer.current = setTimeout(() => setAnchor(null), HIDE_DELAY_MS);
  }, [cancelHide, pinned]);

  useEffect(() => cancelHide, [cancelHide]);

  // Escape and scroll dismiss.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };
    // Opening can scroll the panel (focus / flip). Skip those so the card is not
    // dismissed before the user has moved.
    let skipScroll = true;
    const listen = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        skipScroll = false;
      });
    });
    const onScroll = (event: Event) => {
      if (skipScroll) {
        return;
      }
      const target = event.target;
      if (target instanceof Node && cardRef.current?.contains(target)) {
        return;
      }
      close();
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (cardRef.current?.contains(target) || anchor?.contains(target)) {
        return;
      }
      close();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      cancelAnimationFrame(listen);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [anchor, close, open]);

  return (
    <Box sx={triggerWrapSx}>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          cancelHide();
          if (pinned) {
            close();
            return;
          }
          setPinned(true);
          setAnchor(event.currentTarget);
        }}
        onMouseOver={(event: React.MouseEvent<HTMLElement>) => {
          cancelHide();
          setAnchor(event.currentTarget);
        }}
        onMouseOut={scheduleHide}
        onFocus={(event: React.FocusEvent<HTMLElement>) => setAnchor(event.currentTarget)}
        onBlur={(event: React.FocusEvent<HTMLElement>) => {
          if (pinned || cardRef.current?.contains(event.relatedTarget as Node)) {
            return;
          }
          scheduleHide();
        }}
        sx={triggerButtonSx}
      >
        <Info />
        {label === 'Overall health' ? 'Why this score' : 'Why this reading'}
      </Box>

      <Popper
        open={open}
        anchorEl={popperAnchor}
        placement="top"
        modifiers={[
          { name: 'offset', options: { offset: [0, edgePadding] } },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['bottom'],
              padding: edgePadding,
              boundary: 'viewport',
              rootBoundary: 'viewport',
            },
          },
          {
            name: 'preventOverflow',
            options: {
              padding: edgePadding,
              altAxis: true,
              boundary: 'viewport',
              rootBoundary: 'viewport',
            },
          },
        ]}
        popperOptions={{ strategy: 'fixed' }}
        sx={{
          zIndex: theme.zIndex.app.popper,
          width: panel?.clientWidth,
          maxWidth: panel?.clientWidth,
          ['--reason-popover-max-height' as string]:
            maxHeight !== undefined ? `${maxHeight}px` : undefined,
        }}
      >
        <Box ref={cardRef} onMouseEnter={cancelHide} onMouseLeave={scheduleHide} sx={cardSx}>
          <Box sx={headerSx}>
            <Box component="span" sx={{ minWidth: 0 }}>
              {label}
            </Box>
            <Box sx={headerValueSx}>{value}</Box>
          </Box>

          <Box sx={scrollBodySx}>
            <Box sx={reasoningLabelSx}>
              <Sparkles />
              <span>Reasoning</span>
            </Box>

            <Box sx={reasoningBodySx}>
              {text.map((paragraph) => (
                <p key={paragraphKey(paragraph)}>{paragraph}</p>
              ))}
            </Box>

            {showLoadingCitations ? null : (
              <Box sx={sourceHeadingSx}>
                {showReferences || showUnavailableCitations ? 'References' : 'Source'}
              </Box>
            )}

            {showReferences ? (
              <Box sx={referencesListSx}>
                {references.map((reference) => (
                  <Box
                    key={reference.friendlyId}
                    component="button"
                    type="button"
                    onClick={() =>
                      navigate(portalPaths.conversation.full(reference.friendlyId))
                    }
                    sx={referenceRowSx}
                  >
                    <Box component="span" data-citation-id sx={referenceIdSx}>
                      {reference.friendlyId}
                    </Box>
                    <Box component="span" data-citation-subject sx={referenceSubjectSx}>
                      {reference.subject}
                    </Box>
                    <ArrowUpRight />
                  </Box>
                ))}
              </Box>
            ) : null}
            {!showReferences && !showLoadingCitations ? (
              <Box sx={emptySourceSx}>
                {showUnavailableCitations
                  ? 'Citations unavailable.'
                  : 'Drawn from the site record rather than a conversation.'}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Popper>
    </Box>
  );
};
